import { fashionPaths } from "@/modules";

export interface ProductCarouselQueryPlan {
  key: string;
  request: {
    productIds?: string[];
    offerIds?: string[];
  };
  requestedIds: string[];
  requestedField: "productId" | "offerId";
}

/** Kategorisiz sonuçlarda başarılı sorguyu mevcut keşif ekranına taşır. */
export function productDiscoverPath(
  filters: Record<string, string[]> | undefined,
  discoverQuery?: string
): string {
  const query = discoverQuery?.trim();
  return !filters?.["categories.categoryId"]?.length && query && query !== "*"
    ? fashionPaths.search({ q: query })
    : fashionPaths.search();
}

export interface ProductCarouselQueryResult<T> {
  key: string;
  hits: T[];
}

/** Exact offers are all-or-nothing; older/incomplete payloads retain product hydration. */
export function buildProductCarouselQuery(
  productIds: string[],
  offerMap: Record<string, string> | undefined,
  language: string
): ProductCarouselQueryPlan {
  const mappedOfferIds = productIds.map(
    (productId) => offerMap?.[productId]?.trim() ?? ""
  );
  const exactOfferIds = mappedOfferIds.every(Boolean)
    ? mappedOfferIds
    : undefined;
  return {
    key: `${language}:${productIds.join("|")}:${exactOfferIds?.join("|") ?? "legacy"}`,
    request: exactOfferIds
      ? { offerIds: exactOfferIds }
      : { productIds: [...productIds] },
    requestedIds: exactOfferIds ?? productIds,
    requestedField: exactOfferIds ? "offerId" : "productId",
  };
}

export function orderProductCarouselHits<T extends Record<string, unknown>>(
  hits: T[],
  plan: ProductCarouselQueryPlan
): T[] {
  const rank = new Map(
    plan.requestedIds.map((id, index) => [id, index])
  );
  return [...hits].sort(
    (a, b) =>
      (rank.get(String(a[plan.requestedField] ?? "").trim()) ??
        Number.MAX_SAFE_INTEGER) -
      (rank.get(String(b[plan.requestedField] ?? "").trim()) ??
        Number.MAX_SAFE_INTEGER)
  );
}

/** A result belongs only to the plan that loaded it; stale hits fail closed. */
export function selectProductCarouselHits<T>(
  activePlanKey: string | undefined,
  result: ProductCarouselQueryResult<T> | undefined,
): T[] {
  return activePlanKey && result?.key === activePlanKey ? result.hits : [];
}

/** A cached plan is immediately ready, including after an in-flight replacement aborts. */
export function productCarouselLoadDecision(
  activePlanKey: string,
  fetchedPlanKey: string | undefined,
): { shouldFetch: boolean; loading: boolean } {
  const shouldFetch = activePlanKey !== fetchedPlanKey;
  return { shouldFetch, loading: shouldFetch };
}
